import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";
import TicketListBulkActionBar from "../TicketListBulkActionBar";
import TicketTagPickerModal from "../TicketTagPickerModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import canTransferTicket from "../../helpers/canTransferTicket";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { userCanSeeTicketConnection } from "../../helpers/userCanSeeTicketConnection";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const getUnreadCount = (ticket) => Number(ticket?.unreadMessages) || 0;

// Cria função de ordenação que prioriza fixados > não lidos > data
const createSortFn = (pinnedMap, sortOrderMode, sortDir) => (a, b) => {
    const aPinned = !!pinnedMap[a.id];
    const bPinned = !!pinnedMap[b.id];

    // Fixados sempre no topo
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;

    // Se ambos fixados, ordenar por pinnedAt (mais antigo primeiro = ordem de fixação)
    if (aPinned && bPinned) {
        const aPinnedAt = pinnedMap[a.id] || "";
        const bPinnedAt = pinnedMap[b.id] || "";
        if (aPinnedAt < bPinnedAt) return -1;
        if (aPinnedAt > bPinnedAt) return 1;
        return 0;
    }

    // Não fixados: priorizar não lidos (exceto modo byDate)
    if (sortOrderMode !== "byDate") {
        const aUnread = getUnreadCount(a);
        const bUnread = getUnreadCount(b);
        if (aUnread > 0 && bUnread === 0) return -1;
        if (aUnread === 0 && bUnread > 0) return 1;
    }

    // Ordenar por data
    if (sortDir === "ASC") {
        if (a.updatedAt < b.updatedAt) return -1;
        if (a.updatedAt > b.updatedAt) return 1;
    } else {
        if (a.updatedAt > b.updatedAt) return -1;
        if (a.updatedAt < b.updatedAt) return 1;
    }
    return 0;
};

// Só preserva tags quando o payload omitiu o campo (undefined), não quando veio [] após remoção.
const shouldPreserveTagsField = (incomingTags, existingTags) =>
    incomingTags === undefined &&
    Array.isArray(existingTags) &&
    existingTags.length > 0;

// Merge incoming ticket with existing, preserving tags quando o payload não traz o campo tags
const mergeTicketPreservingTags = (existing, incoming) => {
    const merged = { ...existing, ...incoming };
    if (incoming.contact && existing.contact) {
        merged.contact = { ...existing.contact, ...incoming.contact };
        if (shouldPreserveTagsField(incoming.contact?.tags, existing.contact?.tags)) {
            merged.contact.tags = existing.contact.tags;
        }
    } else if (existing.contact && !incoming.contact) {
        merged.contact = existing.contact;
    }
    if (shouldPreserveTagsField(incoming.tags, existing.tags)) {
        merged.tags = existing.tags;
    }
    return merged;
};

const reducer = (state, action) => {
    const sortDir = action.sortDir;
    const sortOrderMode = action.sortOrderMode || "unreadFirst";
    const pinnedMap = action.pinnedMap || {};
    const sortFn = sortDir && ["ASC", "DESC"].includes(sortDir) ? createSortFn(pinnedMap, sortOrderMode, sortDir) : null;

    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = mergeTicketPreservingTags(state[ticketIndex], ticket);
            } else {
                state.push(ticket);
            }
        });
        if (sortFn) state.sort(sortFn);
        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }
        if (sortFn) state.sort(sortFn);
        return [...state];
    }

    if (action.type === "SET_UNREAD") {
        const { ticketId, unreadMessages } = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = unreadMessages;
        }
        if (sortFn) state.sort(sortFn);
        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = mergeTicketPreservingTags(state[ticketIndex], ticket);
        } else {
            // ✅ Se tem mensagens não lidas, adicionar no topo, senão adicionar no final
            if (getUnreadCount(ticket) > 0) {
                state.unshift(ticket);
            } else {
                state.push(ticket);
            }
        }
        if (sortFn) state.sort(sortFn);
        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            const merged = mergeTicketPreservingTags(state[ticketIndex], ticket);
            state[ticketIndex] = merged;
            // ✅ Se tem mensagens não lidas, mover para o topo
            if (getUnreadCount(ticket) > 0) {
                state.unshift(state.splice(ticketIndex, 1)[0]);
            }
        } else {
            if (action.status === action.payload.status) {
                // ✅ Se tem mensagens não lidas, adicionar no topo
                if (getUnreadCount(ticket) > 0) {
                    state.unshift(ticket);
                } else {
                    state.push(ticket);
                }
            }
        }
        if (sortFn) state.sort(sortFn);
        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            const existing = state[ticketIndex];
            const existingContact = existing.contact;
            const mergedContact = existingContact
                ? { ...existingContact, ...contact }
                : contact;
            if (
                existingContact &&
                shouldPreserveTagsField(contact?.tags, existingContact?.tags)
            ) {
                mergedContact.tags = existingContact.tags;
            }
            state[ticketIndex] = { ...existing, contact: mergedContact };
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }
        if (sortFn) state.sort(sortFn);
        return [...state];
    }

    if (action.type === "REAPPLY_SORT") {
        if (sortFn) state.sort(sortFn);
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        updateUnreadCount,
        updateTotalCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        sortOrderMode: sortOrderModeProp,
        refreshTrigger,
        onTicketStatusChange
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    const sortOrderMode = sortOrderModeProp ?? "unreadFirst";
    let [ticketsList, dispatch] = useReducer(reducer, []);
    const ticketsListRef = useRef([]);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    // Tickets fixados pelo usuário (per-user)
    const [pinnedMap, setPinnedMap] = useState({});
    const pinnedMapRef = useRef({});

    useEffect(() => {
        pinnedMapRef.current = pinnedMap;
    }, [pinnedMap]);

    useEffect(() => {
        const fetchPinned = async () => {
            try {
                const { data } = await api.get("/pinned-tickets");
                setPinnedMap(data || {});
            } catch (err) {
                console.error("Erro ao carregar tickets fixados:", err);
            }
        };
        fetchPinned();
    }, []);

    const handleTogglePin = async (ticketId) => {
        const isPinned = !!pinnedMap[ticketId];
        try {
            if (isPinned) {
                await api.delete(`/pinned-tickets/${ticketId}`);
                setPinnedMap(prev => {
                    const next = { ...prev };
                    delete next[ticketId];
                    return next;
                });
            } else {
                const { data } = await api.post(`/pinned-tickets/${ticketId}`);
                setPinnedMap(prev => ({
                    ...prev,
                    [ticketId]: data.pinnedAt
                }));
            }
            // Re-sort the list after pin change
            dispatch({
                type: "REAPPLY_SORT",
                sortDir: sortTickets,
                sortOrderMode,
                pinnedMap: pinnedMapRef.current
            });
        } catch (err) {
            console.error("Erro ao fixar/desfixar ticket:", err);
        }
    };

    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedTicketIds, setSelectedTicketIds] = useState([]);
    const [bulkTagModalOpen, setBulkTagModalOpen] = useState(false);
    const [bulkKanbanModalOpen, setBulkKanbanModalOpen] = useState(false);
    const [bulkTransferModalOpen, setBulkTransferModalOpen] = useState(false);
    const handleEnterSelectionMode = () => {
        setSelectionMode(true);
        setSelectedTicketIds([]);
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedTicketIds([]);
    };

    const handleToggleSelectTicket = (ticketId) => {
        setSelectedTicketIds((prev) =>
            prev.includes(ticketId)
                ? prev.filter((id) => id !== ticketId)
                : [...prev, ticketId]
        );
    };

    const handleTicketUnreadChange = (ticketId, unreadMessages) => {
        if (unreadMessages > 0) {
            dispatch({
                type: "SET_UNREAD",
                payload: { ticketId, unreadMessages },
                sortDir: sortTickets,
                sortOrderMode,
                pinnedMap: pinnedMapRef.current,
            });
        } else {
            dispatch({
                type: "RESET_UNREAD",
                payload: ticketId,
                sortDir: sortTickets,
                sortOrderMode,
                pinnedMap: pinnedMapRef.current,
            });
        }
    };

    const getSelectedTickets = () =>
        ticketsListRef.current.filter((ticket) =>
            selectedTicketIds.includes(ticket.id)
        );

    const handleBulkAddTag = async (tagId) => {
        const selected = getSelectedTickets();
        const contactIds = [
            ...new Set(
                selected
                    .map((ticket) => ticket.contactId || ticket.contact?.id)
                    .filter(Boolean)
            ),
        ];
        if (!contactIds.length) return;
        const tag = { id: tagId };
        await api.post("/contacts/bulk-tags", { contactIds, tags: [tag] });
        toast.success("Tags adicionadas com sucesso");
        handleExitSelectionMode();
    };

    const handleBulkKanban = async (tagId) => {
        const selected = getSelectedTickets();
        for (const ticket of selected) {
            if (ticket.tags && ticket.tags.length > 0) {
                await api.delete(`/ticket-tags/${ticket.id}`);
            }
            await api.put(`/ticket-tags/${ticket.id}/${tagId}`);
        }
        toast.success("Etapa do Kanban atualizada");
        handleExitSelectionMode();
    };

    const handleBulkCloseTickets = async () => {
        const selected = getSelectedTickets();
        try {
            for (const ticket of selected) {
                await api.put(`/tickets/${ticket.id}`, {
                    status: "closed",
                    userId: user?.id || null,
                    queueId: ticket?.queue?.id || ticket?.queueId || null,
                    sendFarewellMessage: false,
                });
                dispatch({
                    type: "DELETE_TICKET",
                    payload: ticket.id,
                    sortDir: sortTickets,
                    sortOrderMode,
                    pinnedMap: pinnedMapRef.current,
                });
            }
            toast.success("Tickets fechados com sucesso");
            handleExitSelectionMode();
        } catch (err) {
            toastError(err);
        }
    };

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages]);

    useEffect(() => {
        if (sortOrderModeProp !== undefined) {
            dispatch({ type: "REAPPLY_SORT", sortDir: sortTickets, sortOrderMode, pinnedMap: pinnedMapRef.current });
        }
    }, [sortOrderMode, sortTickets, sortOrderModeProp]);

    const { tickets, hasMore, loading, count: totalCount } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        sortOrderMode,
        refreshTrigger
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets,
                sortOrderMode,
                pinnedMap: pinnedMapRef.current
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets, companyId, sortTickets, sortOrderMode, status]);

    // Re-sort quando pinnedMap mudar (pin/unpin)
    useEffect(() => {
        if (Object.keys(pinnedMap).length > 0 || ticketsList.length > 0) {
            dispatch({
                type: "REAPPLY_SORT",
                sortDir: sortTickets,
                sortOrderMode,
                pinnedMap
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pinnedMap]);

    useEffect(() => {
        ticketsListRef.current = ticketsList;
    }, [ticketsList]);

    useEffect(() => {
        const shouldUpdateTicket = ticket => {
            // 🔴 CONEXAO PERMITIDA (Bloco D, espelho do backend).
            // Sem esta linha, o ticket de uma conexao que o atendente NAO pode ver
            // entra na lista ao vivo pelo socket e some no F5 — o backend ja filtra
            // na API, mas o socket entrega direto. A regra e' a MESMA do backend
            // (helper userCanSeeTicketConnection); front e back mudam juntos.
            //
            // Havia um filtro de conexao aqui, COMENTADO (a linha logo abaixo).
            // Ele foi mantido comentado de proposito: usava `user.whatsappId`
            // (conexao padrao de ENVIO) como se fosse permissao de LEITURA — a
            // mesma confusao que fazia o filtro do backend mentir. Nao reative.
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
            if (!userCanSeeTicketConnection(user, ticket)) return false;
            return (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                ((!ticket?.queueId && showTicketWithoutQueue) || selectedQueueIds.indexOf(ticket?.queueId) > -1)
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const belongsToFlowList = (ticket) =>
            ticket && ticket.flowWebhook === true && ticket.flowTriggeredManually === true && (ticket.status === "open" || ticket.status === "pending");

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets,
                    sortOrderMode,
                    pinnedMap: pinnedMapRef.current
                });
            }
            // Aba Flow: ticket tem status "open" no backend; considerar por flowWebhook
            const updateMatchesStatus = status === "flow"
                ? belongsToFlowList(data.ticket)
                : (data.ticket && data.ticket.status === status);
            if (data.action === "update" && data.ticket && updateMatchesStatus) {
                if (shouldUpdateTicket(data.ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                    if (typeof onTicketStatusChange === "function") onTicketStatusChange();
                } else {
                    // ✅ FIX: Ticket não pertence a este usuário/fila — SEMPRE remover
                    // (ex.: ticket transferido para outro atendente ou outra fila)
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: data.ticket.id,
                        status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                    if (typeof onTicketStatusChange === "function") onTicketStatusChange();
                }
            }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets, pinnedMap: pinnedMapRef.current
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets,
                    sortOrderMode,
                    pinnedMap: pinnedMapRef.current
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }

            // Kanban Enterprise: ticket oculto do Chat pelo Kanban
            if (data.action === "ticketHiddenFromChat") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets, sortOrderMode,
                    pinnedMap: pinnedMapRef.current
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }

            // Kanban Enterprise: ticket reapareceu no Chat (nova mensagem do contato)
            if (data.action === "ticketReappearedInChat" && data.ticket) {
                const reappearMatchesStatus = status === "flow"
                    ? belongsToFlowList(data.ticket)
                    : (data.ticket.status === status);
                if (reappearMatchesStatus && shouldUpdateTicket(data.ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                    if (typeof onTicketStatusChange === "function") onTicketStatusChange();
                }
            }
            // create: novo ticket entrou na aba -> adicionar à lista em tempo real
            if (data.action === "create" && data.ticket && shouldUpdateTicket(data.ticket) &&
                (status === "flow" ? belongsToFlowList(data.ticket) : data.ticket.status === status)) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets,
                    sortOrderMode,
                    pinnedMap: pinnedMapRef.current
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            // update: ticket saiu desta aba (ex.: pending -> open) -> remover da lista e atualizar contadores
            if (data.action === "update" && data.ticket && !updateMatchesStatus &&
                (status === "flow" ? !belongsToFlowList(data.ticket) : data.ticket.status !== status)) {
                const ticketInList = ticketsListRef.current.some((t) => t.id === data.ticket.id);
                if (ticketInList) {
                    dispatch({
                        type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                    if (typeof onTicketStatusChange === "function") onTicketStatusChange();
                }
            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            const messageMatchesStatus = status === "flow"
                ? belongsToFlowList(data.ticket)
                : (data.ticket && data.ticket.status === status);
            if (data.action === "create" && messageMatchesStatus) {
                if (shouldUpdateTicket(data.ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET_UNREAD_MESSAGES",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                } else {
                    // ✅ FIX: Mensagem chegou mas ticket não pertence mais a este usuário — remover fantasma
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: data.ticket?.id,
                        status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                    if (typeof onTicketStatusChange === "function") onTicketStatusChange();
                }
            }
            // Atualizar ticket quando mensagem é atualizada (ex: deletada)
            if (data.action === "update" && data.ticket && messageMatchesStatus) {
                if (shouldUpdateTicket(data.ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: data.ticket,
                        status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                } else {
                    // ✅ FIX: SEMPRE remover ticket que não pertence mais ao usuário
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: data.ticket.id,
                        status: status,
                        sortDir: sortTickets,
                        sortOrderMode,
                        pinnedMap: pinnedMapRef.current
                    });
                    if (typeof onTicketStatusChange === "function") onTicketStatusChange();
                }
            }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets,
                    sortOrderMode,
                    pinnedMap: pinnedMapRef.current
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, sortOrderMode, showTicketWithoutQueue, onTicketStatusChange, companyId, socket]);

    // Aplicar mesma lógica de filtragem usada no render para contadores ficarem sincronizados
    const getFilteredList = (list) => {
        if (!status || status === "search") return list;
        if (status === "flow") {
            return list.filter(t => t.flowWebhook === true && t.flowTriggeredManually === true && (t.status === "open" || t.status === "pending"));
        }
        if (status === "open") {
            return list.filter(t => t.status === "open" && !(t.flowWebhook === true && t.flowTriggeredManually === true));
        }
        return list.filter(t => t.status === status);
    };

    useEffect(() => {
        const filtered = getFilteredList(ticketsList);
        if (typeof updateCount === "function") {
            updateCount(filtered.length);
        }
        if (typeof updateUnreadCount === "function") {
            const unreadTickets = filtered.filter(t => Number(t.unreadMessages) > 0).length;
            updateUnreadCount(unreadTickets);
        }
        // Sincronizar contador total: quando a lista diminui (ex.: ticket fechado/ignorado), usar filtered.length para o contador atualizar na hora; quando a lista cresce (socket), usar o maior
        if (typeof updateTotalCount === "function") {
            const total = totalCount !== undefined && totalCount !== null
                ? (filtered.length < totalCount ? filtered.length : Math.max(filtered.length, totalCount))
                : filtered.length;
            updateTotalCount(total);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList, totalCount]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        if (status === "flow") {
            // Aba Flow: apenas fluxo disparado manualmente (não integração automática)
            ticketsList = ticketsList.filter(ticket => ticket.flowWebhook === true && ticket.flowTriggeredManually === true && (ticket.status === "open" || ticket.status === "pending"));
        } else if (status === "open") {
            // Na aba Open não mostrar tickets em fluxo manual (esses só na aba Flow)
            ticketsList = ticketsList.filter(ticket => ticket.status === "open" && !(ticket.flowWebhook === true && ticket.flowTriggeredManually === true));
        } else {
            ticketsList = ticketsList.filter(ticket => ticket.status === status);
        }
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            {bulkTransferModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={bulkTransferModalOpen}
                    onClose={() => setBulkTransferModalOpen(false)}
                    ticketIds={selectedTicketIds}
                    ticket={getSelectedTickets()[0]}
                    onSuccess={() => {
                        selectedTicketIds.forEach((id) => {
                            dispatch({
                                type: "DELETE_TICKET",
                                payload: id,
                                sortDir: sortTickets,
                                sortOrderMode,
                                pinnedMap: pinnedMapRef.current,
                            });
                        });
                        toast.success("Tickets transferidos com sucesso");
                        handleExitSelectionMode();
                    }}
                />
            )}
            <TicketTagPickerModal
                open={bulkTagModalOpen}
                onClose={() => setBulkTagModalOpen(false)}
                kanban={0}
                title="Adicionar tag"
                confirmLabel="Adicionar"
                onSelect={(tagId) => handleBulkAddTag(tagId)}
            />
            <TicketTagPickerModal
                open={bulkKanbanModalOpen}
                onClose={() => setBulkKanbanModalOpen(false)}
                kanban={1}
                title="Mover para etapa do Kanban"
                confirmLabel="Mover"
                onSelect={(tagId) => handleBulkKanban(tagId)}
            />
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0, paddingBottom: selectionMode ? 56 : 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                    isPinned={!!pinnedMap[ticket.id]}
                                    onTogglePin={handleTogglePin}
                                    selectionMode={selectionMode}
                                    isSelected={selectedTicketIds.includes(ticket.id)}
                                    onToggleSelect={handleToggleSelectTicket}
                                    onEnterSelectionMode={handleEnterSelectionMode}
                                    onTicketUnreadChange={handleTicketUnreadChange}
                                />
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
            {selectionMode && (
                <TicketListBulkActionBar
                    selectedCount={selectedTicketIds.length}
                    onClose={handleExitSelectionMode}
                    canTransfer={canTransferTicket(user)}
                    onTransfer={() => {
                        if (!selectedTicketIds.length) {
                            toast.warning("Selecione ao menos um ticket");
                            return;
                        }
                        setBulkTransferModalOpen(true);
                    }}
                    onAddTag={() => {
                        if (!selectedTicketIds.length) {
                            toast.warning("Selecione ao menos um ticket");
                            return;
                        }
                        setBulkTagModalOpen(true);
                    }}
                    onKanban={() => {
                        if (!selectedTicketIds.length) {
                            toast.warning("Selecione ao menos um ticket");
                            return;
                        }
                        setBulkKanbanModalOpen(true);
                    }}
                    onCloseTickets={() => {
                        if (!selectedTicketIds.length) {
                            toast.warning("Selecione ao menos um ticket");
                            return;
                        }
                        handleBulkCloseTickets();
                    }}
                />
            )}
        </Paper>
    );
};

export default TicketsListCustom;
